/**
 * docs/UX.md §3.1 班级看板 — the teacher's home screen.
 *
 * One glance answers "what do I do next": three headline numbers, one sentence
 * of conclusion, one primary action, then the 待办 rows and the student list.
 * No batch ids, no material ids, no internal vocabulary.
 */
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  EmptyState,
  Icon,
  ProgressBar,
  Select,
  Sheet,
  useConfirmSafe,
  useToast,
} from "@xinghai/ui";
import type { IconId, Tone } from "@xinghai/ui";
import { speakersApi, teacherApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import {
  CopyField,
  ErrorNotice,
  LoadingBlock,
  PageHeader,
  Section,
  StatusTag,
  copyText,
  formatDateTime,
} from "./common";
import { CoverageHeatmap } from "./CoverageHeatmap";
import { PublishToClassFlow } from "./PublishToClassFlow";
import { QrCode } from "./QrCode";
import { useAuth } from "../auth/useAuth";
import { Illustration } from "../shared/Illustration";
import type { DashboardClass, DashboardStudent, DashboardStudentStatus } from "./types";

const STATUS_META: Record<DashboardStudentStatus, { label: string; icon: IconId; tone: Tone }> = {
  not_started: { label: "未开始", icon: "ui-clock", tone: "neutral" },
  in_progress: { label: "进行中", icon: "ui-microphone", tone: "info" },
  completed: { label: "已完成", icon: "ui-check-circle", tone: "success" },
  needs_rerecord: { label: "需重录", icon: "ui-refresh", tone: "warning" },
};

/** The one-sentence conclusion under the headline numbers. */
export function conclusionFor(c: DashboardClass | null): string {
  if (!c) return "还没有班级数据。";
  if (c.registered === 0) return "还没有人注册，把邀请码发到课程群里就可以开始。";
  const notStarted = Math.max(0, c.registered - c.started);
  if (notStarted > 0) return `还有 ${notStarted} 人没开始。`;
  if (c.completed_session < c.registered) {
    return `${c.registered - c.completed_session} 人还在录，${c.completed_session} 人已完成本轮。`;
  }
  return "全班都完成本轮了，可以发布下一轮任务。";
}

/** 复制提醒文案 — ready to paste into WeChat. */
export function reminderText(s: DashboardStudent, className: string): string {
  const { done, total } = s.progress;
  if (s.status === "needs_rerecord") {
    return `${s.display_name}同学，你在「${className}」有录音需要重录，请打开小程序看一下「我的录音」里标了「需重录」的那几条，按提示重新录一遍，谢谢！`;
  }
  if (s.status === "not_started") {
    return `${s.display_name}同学，「${className}」的录音任务已经发布了，抽 20 分钟找个安静的地方录一下，谢谢！`;
  }
  return `${s.display_name}同学，你在「${className}」已经完成 ${done}/${total} 条，剩下的有空补一下，谢谢！`;
}

export interface ClassDashboardProps {
  /** Course team / admin get a class selector above the board. */
  showClassSelector?: boolean;
  /** Rendered instead of the page header when the board is embedded. */
  embedded?: boolean;
}

export function ClassDashboard({
  showClassSelector = false,
  embedded = false,
}: ClassDashboardProps) {
  const [classId, setClassId] = useState<string>("");
  const [inviteFor, setInviteFor] = useState<DashboardClass | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);

  const q = useQuery({
    queryKey: qk.teacherDashboard(classId || undefined),
    queryFn: ({ signal }) => teacherApi.dashboard(classId ? { class_id: classId } : {}, signal),
  });
  const { me } = useAuth();

  // A teacher who also sees every class should land on one they teach.
  const classes = useMemo(() => {
    const all = q.data?.classes ?? [];
    const mine = new Set(me?.teaches_class_ids ?? []);
    if (mine.size === 0) return all;
    return [...all.filter((c) => mine.has(c.id)), ...all.filter((c) => !mine.has(c.id))];
  }, [q.data?.classes, me?.teaches_class_ids]);
  const active = useMemo(
    () => classes.find((c) => c.id === classId) ?? classes[0] ?? null,
    [classes, classId],
  );
  const students = useMemo(() => {
    const all = q.data?.students ?? [];
    if (!active || classes.length <= 1) return all;
    return all.filter((s) => !s.class_id || s.class_id === active.id);
  }, [q.data, active, classes.length]);

  if (q.isPending) {
    return (
      <div className={embedded ? "" : "xz-adm"}>
        {!embedded && <PageHeader title="班级看板" />}
        <LoadingBlock rows={3} label="正在加载班级看板" />
      </div>
    );
  }

  if (q.isError) {
    return (
      <div className={embedded ? "" : "xz-adm"}>
        {!embedded && <PageHeader title="班级看板" />}
        <ErrorNotice error={q.error} onRetry={() => void q.refetch()} title="班级看板没能加载" />
      </div>
    );
  }

  const body = (
    <>
      {showClassSelector && classes.length > 0 && (
        <div className="xz-adm__toolbar">
          <label className="xz-picker__label" htmlFor="xz-class-pick">
            班级
          </label>
          <Select
            id="xz-class-pick"
            aria-label="选择班级"
            value={active?.id ?? ""}
            onChange={(e) => setClassId(e.target.value)}
            options={classes.map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>
      )}

      {!active ? (
        <EmptyState
          title="还没有班级"
          description="先建一个班级，把邀请码发给学生，他们注册后就会出现在这里。"
          action={
            <Link className="gz-button gz-button--primary" to="/m/classes">
              去建班级
            </Link>
          }
        />
      ) : (
        <>
          <Section
            title={active.name}
            subtitle={conclusionFor(active)}
            actions={
              <Button
                leadingIcon={<Icon id="ui-upload" size={16} />}
                onClick={() => setPublishOpen(true)}
              >
                发布任务给全班
              </Button>
            }
          >
            <ul className="xz-headline" aria-label="班级概况">
              <li>
                <span className="xz-headline__value gz-type-score">{active.registered}</span>
                <span className="xz-headline__label">已注册</span>
              </li>
              <li>
                <span className="xz-headline__value gz-type-score">{active.started}</span>
                <span className="xz-headline__label">已开始</span>
              </li>
              <li>
                <span className="xz-headline__value gz-type-score">{active.completed_session}</span>
                <span className="xz-headline__label">已完成本轮</span>
              </li>
            </ul>
            <p className="xz-conclusion">
              <Icon id="ui-info" size={20} />
              <span>{conclusionFor(active)}</span>
            </p>
          </Section>

          <TodoRows cls={active} students={students} />

          {/* C3 覆盖热图 —— 谁卡在哪个环节，一眼看完 (docs/UX.md §3.3) */}
          <CoverageHeatmap classId={active.id} />

          <Section
            title="学生"
            subtitle={`${students.length} 人`}
            actions={
              <Button
                variant="secondary"
                leadingIcon={<Icon id="ui-hash" size={16} />}
                onClick={() => setInviteFor(active)}
              >
                邀请码与二维码
              </Button>
            }
          >
            {students.length === 0 ? (
              <EmptyState
                icon={<Illustration name="empty-class" size="md" />}
                title="还没有学生注册"
                description="把班级邀请码或二维码发到课程群里，学生注册后会出现在这里。"
                action={<Button onClick={() => setInviteFor(active)}>看邀请码</Button>}
              />
            ) : (
              <ul className="xz-students">
                {students.map((s) => (
                  <StudentRow key={s.speaker_id} s={s} className={active.name} />
                ))}
              </ul>
            )}
          </Section>
        </>
      )}

      {inviteFor && <InviteCodeSheet cls={inviteFor} onClose={() => setInviteFor(null)} />}
      {publishOpen && active && (
        <PublishToClassFlow classId={active.id} onClose={() => setPublishOpen(false)} />
      )}
    </>
  );

  if (embedded) return <>{body}</>;
  return (
    <div className="xz-adm">
      <PageHeader title="班级看板" description="今天班上的情况，以及需要你处理的事。" />
      {body}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// 待办
// --------------------------------------------------------------------------- //

function TodoRows({ cls, students }: { cls: DashboardClass; students: DashboardStudent[] }) {
  const toast = useToast();
  const confirm = useConfirmSafe();
  const qc = useQueryClient();

  const pending = students.filter((s) => !s.verified);

  /**
   * There is no bulk verify endpoint, so 全部核验 walks the list one PATCH at a
   * time and reports how many went through (a partial failure is not silent).
   */
  const verifyAll = useMutation({
    mutationFn: async () => {
      let done = 0;
      const failed: string[] = [];
      for (const s of pending) {
        try {
          await speakersApi.verify(s.speaker_id);
          done += 1;
        } catch {
          failed.push(s.display_name);
        }
      }
      return { done, failed };
    },
    onSuccess: ({ done, failed }) => {
      if (failed.length === 0) toast.success(`已核验 ${done} 位学生`);
      else toast.error(`核验了 ${done} 位，${failed.length} 位没成功：${failed.join("、")}`);
      void qc.invalidateQueries({ queryKey: ["teacher", "dashboard"] });
      void qc.invalidateQueries({ queryKey: ["speakers"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const askVerifyAll = async () => {
    const ok = await confirm({
      title: `核验这 ${pending.length} 位学生？`,
      message: "核验表示你已经核对过他们填的方言背景和水平。核验后可以随时再改。",
      confirmLabel: "全部核验",
    });
    if (ok) verifyAll.mutate();
  };

  const rows: Array<{
    key: string;
    icon: IconId;
    label: string;
    count: number;
    hint: string;
    action: ReactNode;
  }> = [
    {
      key: "verify",
      icon: "ui-user-switch",
      label: "待核验",
      count: cls.pending_verification || pending.length,
      hint: "自助注册的学生，核对方言与水平后即可入册。",
      action: (
        <span className="xz-adm__rowactions">
          <Button
            size="sm"
            disabled={pending.length === 0}
            loading={verifyAll.isPending}
            onClick={() => void askVerifyAll()}
          >
            全部核验
          </Button>
          <Link className="gz-button xz-button--sm" to="/m/speakers">
            逐个查看
          </Link>
        </span>
      ),
    },
    {
      key: "qc",
      icon: "ui-inbox",
      label: "待质检",
      count: cls.pending_qc,
      hint: "听一遍就能通过或驳回，一条一屏，很快。",
      action: (
        <Link className="gz-button xz-button--sm" to="/m/qc">
          去质检
        </Link>
      ),
    },
    {
      key: "rerecord",
      icon: "ui-refresh",
      label: "需重录的学生",
      count: cls.needs_rerecord,
      hint: "被驳回的录音要学生自己重录，提醒一下他们。",
      action: null,
    },
  ];

  return (
    <Section title="待办" subtitle="需要你处理的事">
      <ul className="xz-todos">
        {rows.map((r) => (
          <li key={r.key} className="xz-todo" data-empty={r.count === 0}>
            <Icon id={r.icon} size={24} />
            <span className="xz-todo__text">
              <span className="xz-todo__label">
                {r.label} <strong className="gz-type-score">{r.count}</strong>
              </span>
              <span className="gz-caption">{r.count === 0 ? "没有待处理的。" : r.hint}</span>
            </span>
            {r.count > 0 && r.action}
          </li>
        ))}
      </ul>
    </Section>
  );
}

// --------------------------------------------------------------------------- //
// 学生
// --------------------------------------------------------------------------- //

function StudentRow({ s, className }: { s: DashboardStudent; className: string }) {
  const toast = useToast();
  const meta = STATUS_META[s.status];
  const ratio = s.progress.total === 0 ? 0 : s.progress.done / s.progress.total;

  const copyReminder = async () => {
    const ok = await copyText(reminderText(s, className));
    if (ok) toast.success("提醒文案已复制，去微信里粘贴发给他/她。");
    else toast.error("浏览器拒绝了复制，请手动选中文案复制。");
  };

  return (
    <li className="xz-student">
      <span className="xz-student__name">
        {s.display_name}
        {!s.verified && (
          <StatusTag icon="ui-clock" tone="warning">
            待核验
          </StatusTag>
        )}
      </span>
      <StatusTag icon={meta.icon} tone={meta.tone}>
        {meta.label}
      </StatusTag>
      <span className="xz-student__progress">
        <ProgressBar
          size="sm"
          value={ratio}
          tone={s.status === "completed" ? "success" : "info"}
          label={`${s.display_name} 的进度 ${s.progress.done}/${s.progress.total}`}
        />
        <span className="gz-caption">
          {s.progress.done}/{s.progress.total}
        </span>
      </span>
      <span className="gz-caption xz-student__seen">
        最近活动 {s.last_active_at ? formatDateTime(s.last_active_at) : "还没有"}
      </span>
      <Button size="sm" variant="ghost" onClick={() => void copyReminder()}>
        复制提醒文案
      </Button>
      <Link className="gz-button xz-button--sm" to={`/m/speakers/${s.speaker_id}/recordings`}>
        回看
      </Link>
    </li>
  );
}

// --------------------------------------------------------------------------- //
// 邀请码 + 二维码
// --------------------------------------------------------------------------- //

function InviteCodeSheet({ cls, onClose }: { cls: DashboardClass; onClose: () => void }) {
  const link = `${globalThis.location?.origin ?? ""}/register?code=${encodeURIComponent(cls.invite_code ?? "")}`;

  return (
    <Sheet
      open
      onClose={onClose}
      title={`${cls.name} 的邀请码`}
      footer={
        <Button variant="secondary" onClick={onClose}>
          关闭
        </Button>
      }
    >
      {!cls.invite_code ? (
        <p className="gz-muted">这个班还没有邀请码，到「班级」页生成一个。</p>
      ) : (
        <div className="xz-form">
          <p className="xz-invite__code xz-adm__mono" data-testid="class-invite-code">
            {cls.invite_code}
          </p>
          <div className="xz-invite__qr">
            <QrCode value={link} size={176} label={`${cls.name} 注册二维码`} />
            <p className="gz-caption">让学生扫码，或直接输入上面的邀请码。</p>
          </div>
          <CopyField label="邀请码" value={cls.invite_code} />
          <CopyField label="注册链接" value={link} hint="发到课程群里，点开就能注册。" />
          {!cls.invite_enabled && (
            <div className="gz-notice" role="status">
              <Icon id="ui-warning" size={20} />
              <span>这个邀请码当前是停用状态，学生暂时注册不了。去「班级」页重新启用。</span>
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}
